// Imports
import { faPrint } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Params, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import Button from '../../../../../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppDispatch } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetRecipeResponse } from '../../../../../../../shareables/types/api/main/website/page/type/213/recipe/get';
import htmlEncodeTemplateLiteral from '../../../../../../../shareables/utils/sanitizing/html-encode-template-literal';
import printHTML from '../../../../../../../shareables/front-end/utils/print-html';
import Byline from '../../shared-components/byline';
import getHumanReadableTime from '../utils/get-human-readable-time';


// Styled components
const Container = styled.div`
	h3 {
		font-size: 1.5rem;
		font-weight: 500;
		line-height: 1.1;
	}
	
	hr {
		margin-top: 1.25rem;
		margin-bottom: 1.25rem;
		border: 0;
		border-top: 0.0625rem solid #eee;
	}
`;

const Heading = styled.h3`
	margin-bottom: 0.625rem;
`;

const Ingredients = styled.ul`
	list-style: none;
	margin-left: 0;
`;

const Directions = styled.p`
	white-space: pre-line;
	margin-bottom: 0.625rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	pageTitle: string;
	storybookRecipe?: GetRecipeResponse;
}


// Function component
const Recipe: React.FC<Props> = ({ pageID, pageTitle, storybookRecipe }) => {
	// Use state
	const [recipe, setRecipe] = useState<GetRecipeResponse | null>(storybookRecipe ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const { recipeIdentifier } = useParams<Params>();
	
	
	// Use ref
	const containerRef = useRef<HTMLDivElement>(null);
	
	
	// Load recipe on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetRecipeResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/213/recipe/${recipeIdentifier ?? ''}`,
				completion: (json) => {
					setRecipe(json.response);
				},
			})
		);
	}, [dispatch, pageID, recipeIdentifier]);
	
	
	// Handle printing
	const handlePrint = () => {
		// Do no work if data is loading
		if (recipe === null || !containerRef.current) {
			return;
		}
		
		
		// Get the current document styles
		const styles = Array.from(document.styleSheets)
			.map((sheet) => {
				try {
					// Get rules
					const rules = Array.from(sheet.cssRules || []);
					
					
					// Return
					return rules.map((rule) => rule.cssText).join('\n');
				} catch (e) {
					// Ignore stylesheets that we can't access
					return '';
				}
			})
			.join('\n');
		
		
		// Modify spacing
		const spacingStyles = `
			body {
				margin: 2rem;
				min-height: unset; /* If not, there will be an additional blank page */
			}
		`;
		
		
		// Add custom styles
		const customStyles = `
						div:first-of-type{
							line-height: 1.1;
						}
						
						h3 {
							margin-bottom: 1rem;
						}
						
						hr {
							margin-top: 1.25rem;
							margin-bottom: 1.25rem;
							border: 0;
							border-top: .0625rem solid #eee;
						}
						
						ul {
							list-style: none;
						}
				
				`;
		
		
		// Get current content
		const currentContent = containerRef.current.innerHTML;
		
		
		// Form HTML content for printing
		const printContent = currentContent
			// Change recipe name heading
			.replace(/<h3>(.*?)<\/h3>/, `<h1>$1</h1>`)
			// Replace "By" line
			.replace(/<small.*?<\/small>/, () =>
				recipe.submitter?.formattedName
					? htmlEncodeTemplateLiteral`<small><em>By ${recipe.submitter.formattedName}</em></small>`
					: ''
			)
			// Remove the print button
			.replace(/<button.*?<\/button>/, '');
		
		
		// Print
		printHTML(
			`<style>${styles}</style><style>${spacingStyles}</style><style>${customStyles}</style><body>${printContent}</body>`
		);
	};
	
	
	// If loading, then display nothing
	if (recipe === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<Container ref={containerRef}>
			<Helmet title={`${recipe.name} | ${pageTitle}`} />
			
			<div>
				<h3>{recipe.name}</h3>
				<Byline className='is-gray' post={recipe} created={recipe.submitted} action='submitted' />
				<Button type='button' color='plain' border smaller onClick={handlePrint}>
					<FontAwesomeIcon icon={faPrint} />
					<span>Print Recipe</span>
				</Button>
			</div>
			
			<hr />
			
			<div>
				<Heading>Time to make</Heading>
				<div>
					Prep time: <strong>{getHumanReadableTime(recipe.prepTime)}</strong>
				</div>
				<div>
					Cook time: <strong>{getHumanReadableTime(recipe.cookTime)}</strong>
				</div>
				<div>
					Total time: <strong>{getHumanReadableTime(recipe.prepTime + recipe.cookTime)}</strong>
				</div>
			</div>
			
			<hr />
			
			<div>
				<Heading>Ingredients</Heading>
				<Ingredients>
					{recipe.ingredients.split('\n').map((ingredient, i) => (
						<li key={i}>{ingredient}</li>
					))}
				</Ingredients>
			</div>
			
			<hr />
			
			<div>
				<Heading>Directions</Heading>
				<Directions>{recipe.directions}</Directions>
			</div>
		</Container>
	);
};

export default Recipe;
